<template>
    <!-- Onglet Réapprovisionnement (remplace « Anti-rupture ») : chargé à sa première ouverture, cache 5 min -->
    <div>
        <div class="pp-minis">
            <div class="pp-mk"><span>En rupture</span><b class="pp-txt-d">{{ compte('rupture') }}</b></div>
            <div class="pp-mk"><span>Tiennent moins de {{ horizon }} j</span><b class="pp-txt-w">{{ compte('bientot') }}</b></div>
            <div class="pp-mk"><span>Sous le stock mini</span><b>{{ compte('min') }}</b></div>
            <div class="pp-mk"><span>Unités à commander</span><b class="pp-violet">{{ nombre(totalACommander) }}</b></div>
        </div>

        <div class="pp-bloc">
            <div class="pp-bloc__tete">
                <div class="pp-filtres">
                    <div class="pp-recherche"><i class="ri-search-line"></i><input v-model="recherche" class="pp-inp" placeholder="Rechercher un article"></div>
                    <button v-for="f in filtres" :key="f.id" type="button" class="pp-chip" :class="{ 'pp-chip--on': filtre === f.id }" @click="filtre = f.id">{{ f.libelle }}</button>
                </div>
                <div class="pp-filtres pp-petit">
                    Couvrir
                    <select v-model.number="horizon" class="pp-select" aria-label="Horizon">
                        <option :value="7">7 jours</option><option :value="14">14 jours</option><option :value="30">30 jours</option>
                    </select>
                    <button type="button" class="ds-btn-ghost !py-1 !px-2 !text-[0.75rem]" :disabled="!filtres_.length" @click="exporterCommande">
                        <i class="ri-file-list-3-line"></i>Exporter la commande
                    </button>
                </div>
            </div>

            <div v-if="chargement && !lignes.length" class="pp-vide"><i class="ri-loader-4-line pp-spin"></i> Analyse des stocks…</div>
            <div v-else-if="erreur" class="pp-vide pp-txt-d">Impossible de charger les stocks.</div>
            <div v-else class="pp-wrap">
                <table class="pp-table">
                    <thead><tr>
                        <th class="th">Article</th><th class="th">Magasin</th><th class="th pp-r">Stock</th><th class="th pp-r">Vendu / jour</th>
                        <th class="th">Tient encore</th><th class="th pp-r">À commander</th><th class="th">Action</th>
                    </tr></thead>
                    <tbody>
                        <tr v-for="x in visibles" :key="x.magpid + '|' + x.articlepid">
                            <td class="td"><b>{{ x.libelle }}</b><small>{{ x.reference || '-' }}</small></td>
                            <td class="td">{{ x.nommaga }}</td>
                            <td class="td pp-r">{{ nombre(x.stock) }}<small v-if="x.stock_min > 0">mini {{ nombre(x.stock_min) }}</small></td>
                            <td class="td pp-r">{{ nombre(x.velocite_jour) }}</td>
                            <td class="td">
                                <div class="pp-barre">
                                    <div class="pp-jauge"><span :style="{ width: Math.max(4, x.couverturePct) + '%', background: x.couleur }"></span></div>
                                    <span v-html="x.tient"></span>
                                </div>
                            </td>
                            <td class="td pp-r"><b>{{ nombre(x.seuil_recommande) }}</b></td>
                            <td class="td"><span class="pp-badge" :class="x.action[1]">{{ x.action[0] }}</span></td>
                        </tr>
                        <tr v-if="!filtres_.length"><td class="td pp-vide" colspan="7">Rien à racheter pour ce filtre.</td></tr>
                    </tbody>
                </table>
                <div v-if="filtres_.length > visibles.length" class="pp-plus">
                    <button type="button" class="ds-btn-ghost !py-1 !px-3 !text-[0.75rem]" @click="nbAffiches += 100">
                        Afficher 100 de plus ({{ visibles.length }} sur {{ filtres_.length }})
                    </button>
                </div>
            </div>
            <p class="pp-legende"><i class="ri-information-line"></i>
                <span>Vendu / jour = ventes moins retours des 30 derniers jours, divisées par 30. Seuls les articles suivis en stock sont analysés : les plats préparés avec une fiche technique sont exclus, leurs matières premières le sont.</span></p>
        </div>
    </div>
</template>

<script>
import { appelPP } from "./appelpp";
import { avecCachePP, TTL_PP_STOCK } from "@/service/cachePrevisionPerformance";

export default {
    name: "TabReappro",
    emits: ["cache", "ruptures"],
    props: {
        magasin: { type: String, default: "" },
        actif: { type: Boolean, default: false },
        forcer: { type: Number, default: 0 }
    },
    data() {
        return {
            chargement: false, erreur: false, aRecharger: true,
            brut: [], horizon: 7, recherche: "", filtre: "tous", nbAffiches: 100,
            filtres: [
                { id: "tous", libelle: "Tous" }, { id: "rupture", libelle: "En rupture" },
                { id: "bientot", libelle: "Sous l'horizon" }, { id: "min", libelle: "Sous le stock mini" }
            ]
        };
    },
    computed: {
        // Une ligne par article à surveiller, triée de la plus urgente à la moins urgente
        lignes() {
            const h = this.horizon;
            return this.brut.map((x) => {
                const jours = x.velocite_jour > 0 ? x.stock / x.velocite_jour : Infinity;
                const etat = x.stock <= 0 && x.rupture_imminente ? "rupture" : x.rupture_imminente ? "bientot" : "min";
                let tient;
                if (x.stock <= 0) tient = '<b class="pp-txt-d">épuisé</b>';
                else if (jours === Infinity) tient = "pas de vente";
                else if (jours < 1) tient = "moins d'un jour";
                else tient = Math.floor(jours) + (jours >= 2 ? " jours" : " jour");
                return {
                    ...x, jours, etat, tient,
                    couverturePct: jours === Infinity ? 100 : Math.min(100, (jours / h) * 100),
                    couleur: x.stock <= 0 ? "rgb(var(--danger))" : jours < h ? "rgb(var(--warning))" : "rgb(var(--info))",
                    action: etat === "rupture" ? ["Commander", "pp-b-d"] : etat === "bientot" ? ["Commander ou transférer", "pp-b-w"] : ["Sous le minimum", "pp-b-m"]
                };
            }).sort((a, b) => a.jours - b.jours);
        },
        filtres_() {
            const q = this.recherche.trim().toLowerCase();
            return this.lignes.filter((x) => (this.filtre === "tous" || x.etat === this.filtre) &&
                (!q || String(x.libelle || "").toLowerCase().includes(q) || String(x.reference || "").toLowerCase().includes(q)));
        },
        visibles() { return this.filtres_.slice(0, this.nbAffiches); },
        totalACommander() { return this.lignes.reduce((s, x) => s + (x.seuil_recommande || 0), 0); }
    },
    watch: {
        actif(v) { if (v && this.aRecharger) this.charger(false); },
        magasin() { this.aRecharger = true; if (this.actif) this.charger(false); },
        forcer() { this.aRecharger = true; if (this.actif) this.charger(true); },
        horizon() { if (this.actif) this.charger(false); }
    },
    mounted() { if (this.actif) this.charger(false); },
    methods: {
        nombre(v) { return Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 }); },
        compte(etat) { return this.lignes.filter((x) => x.etat === etat).length; },
        async charger(force) {
            this.aRecharger = false;
            this.chargement = true;
            this.erreur = false;
            try {
                const params = { magpid: this.magasin || "", horizon_jours: this.horizon };
                const r = await avecCachePP(this.$store.state.appsociete, "anti-rupture", params,
                    () => appelPP(this.$store, "v2/anti-rupture", params), { force, ttl: TTL_PP_STOCK });
                if (!r.data) { this.erreur = true; return; }
                this.brut = r.data.datas || [];
                this.nbAffiches = 100;
                this.$emit("cache", r.cache ? r.t : null);
                this.$emit("ruptures", this.compte("rupture"));
            } catch (e) {
                console.error(e);
                this.erreur = true;
            } finally {
                this.chargement = false;
            }
        },
        exporterCommande() {
            const lignes = [["Reference", "Article", "Magasin", "Stock", "Stock_mini", "Vendu_par_jour", "A_commander", "Action"],
                ...this.filtres_.filter((x) => x.seuil_recommande > 0).map((x) => [x.reference, x.libelle, x.nommaga, x.stock, x.stock_min,
                    Number(x.velocite_jour).toFixed(2), x.seuil_recommande, x.action[0]])];
            const csv = lignes.map((l) => '"' + l.map((v) => String(v ?? "").replace(/"/g, '""')).join('";"') + '"').join("\n");
            const lien = document.createElement("a");
            lien.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
            lien.download = `commande_${this.horizon}j.csv`;
            lien.click();
        }
    }
};
</script>

<style scoped>
.pp-vide { text-align: center; color: #94a3b8; padding: 2.5rem 1rem; }
.pp-spin { display: inline-block; animation: pp-tourne .8s linear infinite; }
@keyframes pp-tourne { to { transform: rotate(360deg); } }
.pp-violet { color: rgb(var(--primary)) !important; }
.pp-minis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: .6rem; margin-bottom: 1rem; }
.pp-mk { padding: .7rem .8rem; border-radius: .5rem; background: #f8fafc; border: 1px solid #e2e8f0; }
.pp-mk span { font-size: .7rem; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: .03em; display: block; }
.pp-mk b { display: block; font-size: 1.15rem; font-variant-numeric: tabular-nums; color: #0f172a; }
.pp-bloc { border: 1px solid #e2e8f0; border-radius: .625rem; background: #fff; }
.pp-bloc__tete { display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; justify-content: space-between; padding: .8rem 1rem; border-bottom: 1px solid #e2e8f0; }
.pp-filtres { display: flex; flex-wrap: wrap; gap: .4rem; align-items: center; }
.pp-petit { font-size: .78rem; color: #64748b; }
.pp-recherche { position: relative; }
.pp-recherche i { position: absolute; left: .6rem; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.pp-inp { height: 34px; border: 1px solid rgb(var(--input-border, 203 213 225)); border-radius: .5rem; padding: 0 .75rem 0 2rem; font-size: .8rem; outline: none; min-width: 13rem; background: #fff; }
.pp-inp:focus, .pp-select:focus { border-color: rgb(var(--primary)); box-shadow: 0 0 0 3px rgb(var(--primary) / .10); }
.pp-select { height: 32px; border: 1px solid rgb(var(--input-border, 203 213 225)); border-radius: .5rem; padding: 0 .5rem; font-size: .78rem; background: #fff; outline: none; }
.pp-chip { border: 1px solid #e2e8f0; background: #fff; border-radius: 99px; padding: .28rem .7rem; font-size: .74rem; font-weight: 600; color: #64748b; cursor: pointer; }
.pp-chip--on { border-color: rgb(var(--primary) / .4); background: rgb(var(--primary) / .08); color: rgb(var(--primary)); }
.pp-wrap { overflow-x: auto; max-height: 620px; overflow-y: auto; }
.pp-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 720px; }
.th { position: sticky; top: 0; z-index: 2; padding: .6rem .8rem; font-weight: 600; font-size: .72rem; color: #1e293b; background: #f1f5f9; border-bottom: 1px solid #e2e8f0; white-space: nowrap; letter-spacing: .02em; text-align: left; }
.td { padding: .55rem .8rem; border-bottom: 1px solid #e2e8f0; white-space: nowrap; font-variant-numeric: tabular-nums; color: #1e293b; }
.td small { display: block; color: #94a3b8; font-size: .7rem; }
.pp-table tr:hover .td { background: #f8fafc; }
.pp-r { text-align: right !important; }
.pp-barre { display: flex; align-items: center; gap: .5rem; font-size: .74rem; }
.pp-jauge { width: 80px; height: 6px; border-radius: 9px; background: #f1f5f9; overflow: hidden; }
.pp-jauge span { display: block; height: 100%; border-radius: 9px; }
.pp-plus { padding: .75rem; text-align: center; border-top: 1px solid #e2e8f0; }
.pp-legende { font-size: .74rem; color: #64748b; display: flex; gap: .4rem; align-items: flex-start; margin: 0; padding: .75rem 1rem; }
.pp-badge { display: inline-flex; align-items: center; font-size: .68rem; font-weight: 700; padding: .15rem .5rem; border-radius: 99px; white-space: nowrap; }
.pp-b-w { background: rgb(var(--warning) / .18); color: #9a6c12; }
.pp-b-d { background: rgb(var(--danger) / .12); color: rgb(var(--danger)); }
.pp-b-m { background: #f1f5f9; color: #64748b; }
.pp-txt-d, :deep(.pp-txt-d) { color: rgb(var(--danger)) !important; }
.pp-txt-w { color: #b7811c !important; }
@media (max-width: 640px) {
    .pp-minis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .pp-recherche, .pp-inp { width: 100%; min-width: 0; }
}
</style>
